import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { stores as storesApi, audits as auditsApi } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import SemaforoBadge from "../components/SemaforoBadge";
import ExcelUploader from "../components/ExcelUploader";
import StartAuditDialog from "../components/StartAuditDialog";
import StoreHistoryDialog from "../components/StoreHistoryDialog";
import { toast } from "sonner";
import {
  Search,
  Trash2,
  PlayCircle,
  Plus,
  Download,
  Store as StoreIcon,
  MapPin,
  ChevronRight,
  LayoutGrid,
  List,
  History,
  SlidersHorizontal,
} from "lucide-react";
import * as XLSX from "xlsx";

const STORE_GRADIENTS = [
  "from-violet-500 to-indigo-600",
  "from-emerald-500 to-teal-600",
  "from-amber-500 to-orange-600",
  "from-sky-500 to-blue-600",
  "from-pink-500 to-rose-600",
  "from-fuchsia-500 to-purple-600",
];

function pickGradient(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return STORE_GRADIENTS[h % STORE_GRADIENTS.length];
}

export default function StoresPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, setActiveStore, setActiveAuditId, viewAllStores, can } = useApp();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("grid");
  const [form, setForm] = useState({ tienda: "", distrito: "", ciudad: "" });
  const [coverOpen, setCoverOpen] = useState(false);
  const [coverStore, setCoverStore] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyStore, setHistoryStore] = useState(null);
  const [scoreOpen, setScoreOpen] = useState(false);
  const [scoreStore, setScoreStore] = useState(null);
  const [scoreForm, setScoreForm] = useState({ score: "", status: "auto" });

  const load = () => storesApi.list(viewAllStores).then(setItems).catch(console.error);
  useEffect(() => { load(); }, [viewAllStores]);

  // Effective traffic light: an explicit manual override wins, otherwise derive
  // from the score (verde ≥85, amarillo ≥70, rojo >0, else pendiente).
  const effStatus = (s) => {
    if (s.status) return s.status;
    const sc = s.score;
    if (typeof sc !== "number" || sc <= 0) return "pendiente";
    return sc >= 85 ? "verde" : sc >= 70 ? "amarillo" : "rojo";
  };

  const filtered = useMemo(() => {
    let arr = items;
    if (statusFilter !== "all") {
      arr = arr.filter((s) => effStatus(s) === statusFilter);
    }
    const v = q.trim().toLowerCase();
    if (v) {
      arr = arr.filter(
        (s) =>
          s.tienda.toLowerCase().includes(v) ||
          (s.distrito || "").toLowerCase().includes(v) ||
          (s.ciudad || "").toLowerCase().includes(v),
      );
    }
    return arr;
  }, [items, q, statusFilter]);

  const startAudit = (s) => {
    if (!can("start_audit")) {
      toast.warning(t("stores.role_error"));
      return;
    }
    setCoverStore(s);
    setCoverOpen(true);
  };

  const onCoverConfirm = async (cover) => {
    if (!coverStore) return;
    try {
      const a = await auditsApi.create({ store_id: coverStore.id, ...cover });
      setActiveStore(coverStore);
      setActiveAuditId(a.id);
      toast.success(t("stores.audit_started", { store: coverStore.tienda }));
      setCoverOpen(false);
      navigate(`/audit/${a.id}`);
    } catch (e) {
      console.error(e);
      toast.error(t("stores.audit_start_error"));
    }
  };

  const onParsed = async (rows) => {
    const list = rows
      .map((r) => ({
        tienda: r.Tienda || r.tienda || r.TIENDA || "",
        distrito: r.Distrito || r.distrito || r.Region || r.region || r["Distrito/Región"] || "",
        ciudad: r.Ciudad || r.ciudad || "",
      }))
      .filter((x) => x.tienda);
    if (!list.length) {
      toast.error(t("stores.headers_error"));
      return;
    }
    await storesApi.bulk(list, false);
    toast.success(t("stores.imported", { n: list.length }));
    load();
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([
      { Tienda: "Sucursal Centro", Distrito: "Norte", Ciudad: "Monterrey" },
      { Tienda: "Sucursal Plaza", Distrito: "Sur", Ciudad: "Saltillo" },
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tiendas");
    XLSX.writeFile(wb, "plantilla_tiendas.xlsx");
  };

  const onCreate = async () => {
    if (!form.tienda) return toast.error(t("stores.required"));
    await storesApi.create(form);
    toast.success(t("stores.added"));
    setOpen(false);
    setForm({ tienda: "", distrito: "", ciudad: "" });
    load();
  };

  const onDelete = async (id, name) => {
    if (!window.confirm(t("stores.confirm_delete", { name }))) return;
    await storesApi.remove(id);
    load();
  };

  const openScore = (s) => {
    setScoreStore(s);
    setScoreForm({
      score: typeof s.score === "number" ? String(s.score) : "",
      status: s.status || "auto",
    });
    setScoreOpen(true);
  };

  const saveScore = async () => {
    if (!scoreStore) return;
    const raw = String(scoreForm.score).trim();
    if (raw === "" || isNaN(Number(raw)) || Number(raw) < 0 || Number(raw) > 100) {
      return toast.error(t("stores.score_invalid"));
    }
    try {
      await storesApi.updateScore(scoreStore.id, { score: Number(raw), status: scoreForm.status });
      toast.success(t("stores.score_updated"));
      setScoreOpen(false);
      setScoreStore(null);
      load();
    } catch (e) {
      console.error(e);
      toast.error(t("stores.score_error"));
    }
  };

  const canManage = can("edit_stores");
  const canAudit = can("start_audit");

  const filters = [
    { id: "all", label: t("stores.filter_all"), count: items.length },
    { id: "verde", label: t("stores.filter_green"), count: items.filter((s) => effStatus(s) === "verde").length },
    { id: "amarillo", label: t("stores.filter_yellow"), count: items.filter((s) => effStatus(s) === "amarillo").length },
    { id: "rojo", label: t("stores.filter_red"), count: items.filter((s) => effStatus(s) === "rojo").length },
    { id: "pendiente", label: t("stores.filter_pending"), count: items.filter((s) => effStatus(s) === "pendiente").length },
  ];

  return (
    <div data-testid="stores-page">
      {/* Hero */}
      <section className="hero-night text-white">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-14">
          <div className="canales-overline text-stone-300">{t("stores.title")}</div>
          <h1 className="font-display text-4xl md:text-6xl mt-3 leading-[1.05]">
            {t("stores.hero_title")}
          </h1>
          <p className="text-stone-300 mt-4 max-w-2xl">
            {t("stores.hero_desc")}
          </p>

          <div className="hero-search mt-8 max-w-2xl flex items-center gap-3">
            <Search size={18} className="text-stone-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("stores.search_placeholder")}
              data-testid="store-search-input"
            />
            {canManage && (
              <ExcelUploader onParsed={onParsed} testid="stores-excel" label={t("stores.import")} />
            )}
          </div>
        </div>
      </section>

      <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-8">
        {/* Toolbar */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            {filters.map((f) => (
              <button
                key={f.id}
                onClick={() => setStatusFilter(f.id)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  statusFilter === f.id
                    ? "bg-[#0B1220] text-white"
                    : "bg-white border border-stone-200 text-stone-700 hover:border-stone-300"
                }`}
                data-testid={`filter-${f.id}`}
              >
                {f.label} <span className="opacity-60 ml-1">{f.count}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden md:flex border border-stone-200 rounded-full p-0.5 bg-white">
              <button
                onClick={() => setView("grid")}
                className={`p-1.5 rounded-full ${view === "grid" ? "bg-stone-100" : ""}`}
                data-testid="view-grid"
              >
                <LayoutGrid size={14} />
              </button>
              <button
                onClick={() => setView("list")}
                className={`p-1.5 rounded-full ${view === "list" ? "bg-stone-100" : ""}`}
                data-testid="view-list"
              >
                <List size={14} />
              </button>
            </div>
            <Button variant="outline" onClick={downloadTemplate} className="gap-2 rounded-full" data-testid="download-template-button">
              <Download size={14} /> {t("stores.template")}
            </Button>
            {canManage && (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button className="brand-button gap-2" data-testid="add-store-button">
                    <Plus size={14} /> {t("stores.new_store")}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t("stores.new_store")}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <label className="canales-overline">{t("common.store")}</label>
                      <Input
                        data-testid="store-name-input"
                        value={form.tienda}
                        onChange={(e) => setForm({ ...form, tienda: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="canales-overline">{t("common.district")}</label>
                      <Input
                        data-testid="store-district-input"
                        value={form.distrito}
                        onChange={(e) => setForm({ ...form, distrito: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="canales-overline">{t("common.city")}</label>
                      <Input
                        data-testid="store-city-input"
                        value={form.ciudad}
                        onChange={(e) => setForm({ ...form, ciudad: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={onCreate} className="brand-button" data-testid="store-save-button">
                      {t("common.save")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Empty state */}
        {filtered.length === 0 && (
          <Card className="canales-card mt-8 p-12 text-center">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#E5EBFA] grid place-items-center text-[#1E3A8A]">
              <StoreIcon size={24} />
            </div>
            <h3 className="font-display text-2xl mt-4">{t("stores.empty_title")}</h3>
            <p className="text-stone-500 text-sm mt-2 max-w-md mx-auto">
              {canManage ? t("stores.empty_admin") : t("stores.empty_other")}
            </p>
            {canManage && (
              <div className="mt-5 flex justify-center gap-2 flex-wrap">
                <ExcelUploader onParsed={onParsed} testid="stores-excel-empty" label={t("stores.import_excel")} />
                <Button onClick={() => setOpen(true)} className="brand-button gap-2">
                  <Plus size={14} /> {t("stores.new_store")}
                </Button>
              </div>
            )}
          </Card>
        )}

        {/* Grid view */}
        {filtered.length > 0 && view === "grid" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-6" data-testid="stores-grid">
            {filtered.map((s, i) => (
              <Card
                key={s.id}
                className="canales-card overflow-hidden group fade-up"
                style={{ animationDelay: `${Math.min(i, 8) * 30}ms` }}
                data-testid={`store-card-${s.tienda}`}
              >
                <div className={`relative h-28 bg-gradient-to-br ${pickGradient(s.tienda)} flex items-end p-4`}>
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.3),transparent_60%)]" />
                  <div className="relative z-10">
                    <div className="w-9 h-9 rounded-lg bg-white/20 backdrop-blur grid place-items-center text-white">
                      <StoreIcon size={18} />
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold truncate" title={s.tienda}>{s.tienda}</h3>
                      <div className="flex items-center gap-1.5 text-xs text-stone-500 mt-1">
                        <MapPin size={12} /> {s.ciudad || "—"} {s.distrito ? `· ${s.distrito}` : ""}
                      </div>
                    </div>
                    {typeof s.score === "number" && s.score > 0 ? (
                      <SemaforoBadge score={s.score} semaforo={s.status || undefined} />
                    ) : (
                      <span className="pill text-[10px]">{t("stores.no_audited")}</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-5 pt-4 border-t border-stone-100">
                    {canAudit ? (
                      <Button
                        size="sm"
                        onClick={() => startAudit(s)}
                        className="brand-button gap-1 px-4 h-9"
                        data-testid={`start-audit-${s.tienda}`}
                      >
                        <PlayCircle size={14} /> {t("stores.audit")}
                      </Button>
                    ) : (
                      <span className="text-xs text-stone-400">{t("stores.readonly")}</span>
                    )}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => { setHistoryStore(s); setHistoryOpen(true); }}
                        className="p-2 text-stone-400 hover:text-[#1E3A8A]"
                        title={t("assignments.store_history")}
                        data-testid={`store-history-${s.tienda}`}
                      >
                        <History size={14} />
                      </button>
                      {canManage && (
                        <button
                          onClick={() => openScore(s)}
                          className="p-2 text-stone-400 hover:text-[#1E3A8A]"
                          title={t("stores.edit_score")}
                          data-testid={`edit-store-score-${s.tienda}`}
                        >
                          <SlidersHorizontal size={14} />
                        </button>
                      )}
                      {canManage && (
                        <button
                          onClick={() => onDelete(s.id, s.tienda)}
                          className="p-2 text-stone-400 hover:text-red-600"
                          data-testid={`delete-store-${s.tienda}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* List view */}
        {filtered.length > 0 && view === "list" && (
          <Card className="canales-card mt-6 overflow-hidden">
            <ul className="divide-y divide-stone-100" data-testid="stores-list">
              {filtered.map((s) => (
                <li key={s.id} className="p-4 flex items-center justify-between gap-3 hover:bg-stone-50 transition" data-testid={`store-row-${s.tienda}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`w-9 h-9 rounded-lg bg-gradient-to-br ${pickGradient(s.tienda)} grid place-items-center text-white shrink-0`}>
                      <StoreIcon size={16} />
                    </span>
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{s.tienda}</div>
                      <div className="text-xs text-stone-500 truncate">{s.distrito || "—"} · {s.ciudad || "—"}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {typeof s.score === "number" && s.score > 0 ? (
                      <SemaforoBadge score={s.score} semaforo={s.status || undefined} />
                    ) : (
                      <span className="pill text-[10px]">{t("stores.no_audited")}</span>
                    )}
                    {canAudit && (
                      <Button
                        size="sm"
                        onClick={() => startAudit(s)}
                        className="brand-button gap-1 h-8"
                        data-testid={`start-audit-list-${s.tienda}`}
                      >
                        {t("stores.audit")} <ChevronRight size={14} />
                      </Button>
                    )}
                    <button
                      onClick={() => { setHistoryStore(s); setHistoryOpen(true); }}
                      className="p-2 text-stone-400 hover:text-[#1E3A8A]"
                      title={t("assignments.store_history")}
                    >
                      <History size={14} />
                    </button>
                    {canManage && (
                      <button
                        onClick={() => openScore(s)}
                        className="p-2 text-stone-400 hover:text-[#1E3A8A]"
                        title={t("stores.edit_score")}
                        data-testid={`edit-store-score-list-${s.tienda}`}
                      >
                        <SlidersHorizontal size={14} />
                      </button>
                    )}
                    {canManage && (
                      <button
                        onClick={() => onDelete(s.id, s.tienda)}
                        className="p-2 text-stone-400 hover:text-red-600"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>

      <StartAuditDialog
        open={coverOpen}
        onOpenChange={setCoverOpen}
        store={coverStore}
        onConfirm={onCoverConfirm}
      />

      <StoreHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        store={historyStore}
      />

      <Dialog open={scoreOpen} onOpenChange={setScoreOpen}>
        <DialogContent data-testid="edit-score-dialog">
          <DialogHeader>
            <DialogTitle>{t("stores.edit_score")}</DialogTitle>
            <DialogDescription>
              {scoreStore?.tienda} — {t("stores.edit_score_hint")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-stone-600">{t("stores.score_label")}</label>
              <Input
                type="number"
                min={0}
                max={100}
                step="0.1"
                data-testid="store-score-input"
                value={scoreForm.score}
                onChange={(e) => setScoreForm((f) => ({ ...f, score: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600">{t("stores.semaphore_label")}</label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {[
                  { id: "auto", label: t("stores.semaphore_auto"), cls: "bg-stone-900 text-white border-stone-900" },
                  { id: "verde", label: t("stores.filter_green"), cls: "bg-emerald-600 text-white border-emerald-600" },
                  { id: "amarillo", label: t("stores.filter_yellow"), cls: "bg-amber-500 text-white border-amber-500" },
                  { id: "rojo", label: t("stores.filter_red"), cls: "bg-red-600 text-white border-red-600" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setScoreForm((f) => ({ ...f, status: opt.id }))}
                    data-testid={`store-status-${opt.id}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      scoreForm.status === opt.id ? opt.cls : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveScore} className="brand-button" data-testid="save-store-score">
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
